/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BladeModel } from 'src/core/components/BladeModel';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import { LuisId } from 'src/luis/types/Resource';
import { QnaMakerChatTestBlade } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerChatTestBlade';
import { QnaMakerDetailedBlade } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerDetailedBlade';
import { QnaMakerPredictedQnA } from 'src/qnaMaker/core/types/QnaMakerPredictedQnA';

type QnATestPaneBlades = 'Chat' | 'DetailedQnA';
type QnATestPaneProps = {
    isOpen: boolean;
    onDismiss: () => void;
    onEditQnA: (answerId: number) => void;
    onAddQuestion: (answerId: number, questions: string[]) => void;
    flattenMetadata: Set<string>;
};

const messages = defineMessages({ test: { id: 'qna.qnATestPane.test', defaultMessage: 'Test' } });

const headerTheme = getScopedTheme('GlobalHeaderBlades');

export const QnaMakerTestPane = observer((props: QnATestPaneProps) => {
    const { isOpen, onDismiss, onAddQuestion, onEditQnA, flattenMetadata } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [bladeIds, setBladeIds] = React.useState<QnATestPaneBlades[]>(['Chat']);
    const [selectedQna, setSelectedQna] = React.useState<QnaMakerPredictedQnA>(null);

    const openDetailedViewForQna = (qnaId: LuisId) => {
        setSelectedQna(testPanePredictionStoreHouse.findById(qnaId));
        if (bladeIds.indexOf('DetailedQnA') < 0) {
            setBladeIds([...bladeIds, 'DetailedQnA']);
        }
    };

    const {
        currentQnaMakerProjectStore: { testPanePredictionStoreHouse, defaultAnswer }
    } = rootStore;
    const blades = bladeIds.map<BladeModel>(bladeId => {
        switch (bladeId) {
            case 'DetailedQnA':
                return {
                    width: 520,
                    key: bladeId,
                    header: <TestBladeHeader />,
                    content: (
                        <QnaMakerDetailedBlade
                            qna={selectedQna}
                            defaultAnswer={defaultAnswer}
                            onEditQnA={onEditQnA}
                            onAddQuestion={onAddQuestion}
                        />
                    )
                };
            case 'Chat':
            default:
                return {
                    key: bladeId,
                    header: <TestBladeHeader>{localize(messages.test)}</TestBladeHeader>,
                    content: <QnaMakerChatTestBlade onInspectClick={openDetailedViewForQna} flattenMetadata={flattenMetadata} />
                };
        }
    });

    const popBlade = () => {
        if (bladeIds.length === 1) {
            onDismiss();

            return;
        }

        setBladeIds(bladeIds.slice(0, bladeIds.length - 1));
    };

    return (
        <StackBladeHost
            isOpen={isOpen}
            blades={blades}
            onDismiss={popBlade}
            onLastBladeDismissed={popBlade}
            topSpacing={headerTheme.topSpacing}
        />
    );
});
